import {
    APP,
} from '_pixiSrc/globals';
import Sprite from '_pixiSrc/display/Sprite';
import { MOBILE } from '_pixiSrc/layout/features';
import Static from '../libraries/Static';
import AudioManager from './AudioManager';


/**
 * @class Button
 * @augments Sprite
 * @memberOf module:common
 * @classdesc This class provide basic button interaction and states for display sprite as button.
 */
class Button extends Sprite {
    /**
   * @param {String} path asset path for button
   * @param {Number} [x=0]
   * @param {Number} [y=0]
   * @param {Number} [scale=1]
   * @param {Function} [clickAction=()=> {}] call on user click
   * @param {Object} [buttonStates={}] pass data for which state to display on user interection for button
   */
    constructor(path, x = 0, y = 0, scale = 1, clickAction = () => { /* empty */ }, buttonStates = {}) {
        const asset = APP.library.getAsset(path);
        super(asset, asset.width, asset.height, asset.frames, asset.layers);

        this.position.set(x, y);

        this.buttonStates = {
            normal: !isNaN(buttonStates.normal) ? buttonStates.normal : 0,
            hover: !isNaN(buttonStates.hover) ? buttonStates.hover : 1,
            disable: !isNaN(buttonStates.disable) ? buttonStates.disable : 2,
            pressed: !isNaN(buttonStates.pressed) ? buttonStates.pressed : 3,
        };

        this.gotoAndStop(this.buttonStates.normal);

        this.scale.set(scale);

        this.clickAction = clickAction;

        this.isDisabled = false;
        this.isPointerDown = false;
        this.interactive = true;
        this.buttonMode = true;
        this.cursor = 'pointer';

        if (MOBILE) {
            this.once('added', this._initMobile, this);
        } else {
            this.once('added', this._init, this);
        }
    }

    /**
   * It enable necessary interactive event and handle them for only mobile devices
   */
    _initMobile() {
        this.on('pointerdown', (e) => {
            if (this.isDisabled) {
                return;
            }
            this.isPointerDown = true;
            this.gotoAndStop(this.buttonStates.pressed);
            this.onClickHandler();
            e.stopPropagation();
        });

        this.on('pointerupoutside', () => {
            if (this.isDisabled) {
                return;
            }
            this.isPointerDown = false;
            this.gotoAndStop(this.buttonStates.normal);
        });

        this.on('pointerup', () => {
            if (this.isDisabled) {
                return;
            }
            this.isPointerDown = false;
            this.gotoAndStop(this.buttonStates.normal);
        });
    }

    /**
   * @name _init
   * @description It enable necessary interactive event and handle them.
   */
    _init() {
        this.on('pointerclick', (e) => {
            if (this.isDisabled) {
                return;
            }

            this.onClickHandler();
            e.stopPropagation();
        });

        this.on('pointerover', () => {
            if (this.isDisabled) {
                return;
            }
            if (this.isPointerDown) {
                this.gotoAndStop(this.buttonStates.pressed);
            } else {
                this.gotoAndStop(this.buttonStates.hover);
            }
        });

        this.on('pointerout', () => {
            if (this.isDisabled) {
                return;
            }
            this.gotoAndStop(this.buttonStates.normal);
        });

        this.on('pointerdown', () => {
            if (this.isDisabled) {
                return;
            }
            this.isPointerDown = true;
            this.gotoAndStop(this.buttonStates.pressed);
        });

        this.on('pointerupoutside', () => {
            if (this.isDisabled) {
                return;
            }
            this.isPointerDown = false;
            this.gotoAndStop(this.buttonStates.normal);
        });

        this.on('pointerup', () => {
            if (this.isDisabled) {
                return;
            }
            this.isPointerDown = false;
            this.gotoAndStop(this.buttonStates.hover);
        });
    }

    /**
 * @name onClickHandler
 * @description it handle click event and do not call click action if button is disable
 */
    onClickHandler() {
        if (this.isDisabled) {
            return;
        }

        AudioManager.playSound('fx', 'btns');
        this.clickAction();
    }

    /**
       * @name writeText
       * @param  {Array} rest write text on button using bitmap text
       */
    writeText(...rest) {
        this.text = Static.writeText(this, ...rest);
    }

    /**
   * @name disable
   * @description set button to disable
   */
    disable() {
        this.isDisabled = true;
        this.gotoAndStop(this.buttonStates.disable);
    }

    /**
   * @name enable
   * @description set button to enable
   */
    enable() {
        this.isDisabled = false;
        this.gotoAndStop(this.buttonStates.normal);
    }

    /**
   * @name updateState
   * @memberof Button
   * @description display buttons frame as its different states
   */
    updateState() {
        if (this.isDisabled) {
            this.gotoAndStop(this.buttonStates.disable);
        } else if (this.isPointerDown) {
            this.gotoAndStop(this.buttonStates.pressed);
        } else {
            this.gotoAndStop(this.buttonStates.normal);
        }
    }
}

export default Button;
